import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

const MIN_FONT = 12;
const MAX_FONT = 56;
const ROTATIONS = [0, 0, 0, 90, -90, 45, -45, 25, -25, 15];
const HOVER_FILL = '#00C2C2';
const TOOLTIP_OFFSET = 12;
const TOOLTIP_EST_W = 200;
const TOOLTIP_EST_H = 88;

function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** reply_rate 0 → slate; high → emerald */
export function colorForReplyRate(replyRate) {
  const t = Math.max(0, Math.min(1, Number(replyRate) / 100));
  if (t <= 0) return '#94a3b8';
  if (t >= 0.85) return '#34d399';
  const r0 = 148;
  const g0 = 163;
  const b0 = 184;
  const r1 = 52;
  const g1 = 211;
  const b1 = 153;
  const r = Math.round(r0 + (r1 - r0) * t);
  const g = Math.round(g0 + (g1 - g0) * t);
  const b = Math.round(b0 + (b1 - b0) * t);
  return `rgb(${r},${g},${b})`;
}

function estimateBox(text, fontSize, rotateDeg) {
  const w = Math.max(fontSize * 0.45, text.length * fontSize * 0.52);
  const h = fontSize * 1.15;
  const rad = (rotateDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  return { w: w * cos + h * sin, h: w * sin + h * cos };
}

/** Deterministic spiral layout — computed once per items + size, never on hover. */
function layoutWords(items, width, height) {
  if (!width || !height || !items?.length) return [];
  const weights = items.map((w) => Number(w.weight) || 0);
  const maxW = Math.max(...weights, 1);
  const minW = Math.min(...weights, 0);
  const sorted = [...items].sort((a, b) => (Number(b.weight) || 0) - (Number(a.weight) || 0));
  const placed = [];
  const boxes = [];
  const cx = width / 2;
  const cy = height / 2;

  for (const word of sorted) {
    const wgt = Number(word.weight) || 0;
    const t = maxW === minW ? 1 : (wgt - minW) / (maxW - minW);
    const fontSize = MIN_FONT + t * (MAX_FONT - MIN_FONT);
    const rotate = ROTATIONS[hashString(word.text) % ROTATIONS.length];
    const { w, h } = estimateBox(word.text, fontSize, rotate);

    let placedOk = false;
    for (let i = 0; i < 1200 && !placedOk; i += 1) {
      const angle = i * 0.42;
      const radius = 4 + i * 1.6;
      const x = cx + Math.cos(angle) * radius - w / 2;
      const y = cy + Math.sin(angle) * radius - h / 2;
      const inBounds = x >= 2 && y >= 2 && x + w <= width - 2 && y + h <= height - 2;
      const collides = boxes.some(
        (b) => !(x + w < b.x || b.x + b.w < x || y + h < b.y || b.y + b.h < y),
      );
      if (inBounds && !collides) {
        boxes.push({ x, y, w, h });
        placed.push({
          text: word.text,
          messages: word.messages ?? 0,
          open_rate: word.open_rate ?? 0,
          reply_rate: word.reply_rate ?? 0,
          x: x + w / 2,
          y: y + h / 2,
          fontSize,
          rotate,
          fill: colorForReplyRate(word.reply_rate),
        });
        placedOk = true;
      }
    }
  }
  return placed;
}

export function normalizeKeywordList(raw) {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .map((item) => {
      if (typeof item === 'string') {
        const text = item.trim();
        return text ? { text, weight: 1, messages: 0, open_rate: 0, reply_rate: 0 } : null;
      }
      const text = String(item.text ?? item.word ?? item.keyword ?? item.term ?? '').trim();
      if (!text) return null;
      return {
        text,
        weight:
          Number(item.weight ?? item.count ?? item.messages ?? item.total ?? item.frequency) || 1,
        messages:
          Number(item.messages ?? item.message_count ?? item.total_sent ?? item.count) || 0,
        open_rate: Number(item.open_rate) || 0,
        reply_rate: Number(item.reply_rate) || 0,
      };
    })
    .filter(Boolean);
}

export function parseKeywordsPayload(data) {
  if (!data || typeof data !== 'object') {
    return { subject: [], body: [] };
  }
  return {
    subject: normalizeKeywordList(
      data.subject_keywords ??
        data.subject_lines ??
        data.by_subject ??
        data.subject ??
        data.subjects,
    ),
    body: normalizeKeywordList(
      data.body_keywords ?? data.message_body ?? data.by_body ?? data.body ?? data.bodies,
    ),
  };
}

function fmtPct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0.0';
  return v.toFixed(1);
}

function clampTooltipPosition(x, y, width, height) {
  let left = x + TOOLTIP_OFFSET;
  let top = y + TOOLTIP_OFFSET;
  if (left + TOOLTIP_EST_W > width) left = x - TOOLTIP_EST_W - TOOLTIP_OFFSET;
  if (top + TOOLTIP_EST_H > height) top = y - TOOLTIP_EST_H - TOOLTIP_OFFSET;
  return {
    left: Math.max(8, Math.min(left, width - TOOLTIP_EST_W - 8)),
    top: Math.max(8, Math.min(top, height - TOOLTIP_EST_H - 8)),
  };
}

export default function MessageKeywordsWordCloud({
  title,
  items,
  emptyLabel,
  tooltipLabels,
  loading = false,
  height = 280,
}) {
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [tooltipWord, setTooltipWord] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const { width, height: h } = entries[0]?.contentRect ?? {};
      if (width > 0 && h > 0) setSize({ width, height: h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const normalizedItems = useMemo(() => normalizeKeywordList(items), [items]);

  const placedWords = useMemo(
    () => layoutWords(normalizedItems, size.width, size.height),
    [normalizedItems, size.width, size.height],
  );

  const hasItems = normalizedItems.length > 0;
  const showCloud = hasItems && size.width > 0 && size.height > 0 && placedWords.length > 0;
  const positionTooltip = (event) => {
    const container = containerRef.current;
    const tooltip = tooltipRef.current;
    if (!container || !tooltip) return;
    const rect = container.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const { left, top } = clampTooltipPosition(x, y, size.width, size.height);
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  };

  const showTooltip = (word, event) => {
    setTooltipWord(word);
    const tooltip = tooltipRef.current;
    if (tooltip) tooltip.style.display = 'block';
    positionTooltip(event);
  };

  const hideTooltip = () => {
    setTooltipWord(null);
    const tooltip = tooltipRef.current;
    if (tooltip) tooltip.style.display = 'none';
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 min-w-0">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</h4>
      <div
        ref={containerRef}
        className="relative w-full rounded-md bg-slate-50/80 border border-gray-100"
        style={{ height }}
      >
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-50/90 rounded-md">
            <Loader2 className="h-6 w-6 animate-spin text-[#00C2C2]" aria-hidden />
          </div>
        )}
        {!loading && !hasItems ? (
          <p className="absolute inset-0 flex items-center justify-center text-xs text-gray-500 italic px-4 text-center">
            {emptyLabel}
          </p>
        ) : showCloud ? (
          <svg
            width={size.width}
            height={size.height}
            className="block rounded-md [&_text.kw-cloud-word]:cursor-default [&_text.kw-cloud-word]:select-none"
            role="img"
            aria-label={title}
          >
            <style>{`.kw-cloud-word:hover { fill: ${HOVER_FILL} !important; }`}</style>
            {placedWords.map((w, idx) => (
              <text
                key={`${w.text}-${idx}`}
                className="kw-cloud-word"
                x={w.x}
                y={w.y}
                fill={w.fill}
                fontSize={w.fontSize}
                fontWeight={600}
                fontFamily="system-ui, sans-serif"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${w.rotate}, ${w.x}, ${w.y})`}
                onMouseEnter={(e) => showTooltip(w, e)}
                onMouseMove={positionTooltip}
                onMouseLeave={hideTooltip}
              >
                {w.text}
              </text>
            ))}
          </svg>
        ) : null}
        <div
          ref={tooltipRef}
          className="pointer-events-none absolute z-20 hidden max-w-xs rounded-md bg-[#0A1A2F] text-white px-3 py-2 shadow-lg"
        >
          {tooltipWord && (
            <>
              <p className="text-sm font-semibold text-[#00C2C2] mb-1">{tooltipWord.text}</p>
              <p className="text-xs">
                {tooltipLabels.messages}: {tooltipWord.messages}
              </p>
              <p className="text-xs">
                {tooltipLabels.open}: {fmtPct(tooltipWord.open_rate)}%
              </p>
              <p className="text-xs">
                {tooltipLabels.reply}: {fmtPct(tooltipWord.reply_rate)}%
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
